import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

const COLORS = { red: "#ff2d2d", blue: "#2e6bff", white: "#f5f5f5", amber: "#ffb02e", green: "#00e676", cyan: "#22d3ee" };
export const hexOf = (id) => COLORS[id] || COLORS.white;

export const MAT = {
  housing: () => new THREE.MeshStandardMaterial({ color: "#14161a", metalness: 0.55, roughness: 0.38 }),
  housingMatte: () => new THREE.MeshStandardMaterial({ color: "#0c0d10", metalness: 0.3, roughness: 0.6 }),
  chrome: () => new THREE.MeshStandardMaterial({ color: "#c9ced6", metalness: 1, roughness: 0.16 }),
  reflector: () => new THREE.MeshStandardMaterial({ color: "#dfe3ea", metalness: 1, roughness: 0.08 }),
  lens: () => new THREE.MeshPhysicalMaterial({ color: "#bcd2e8", transparent: true, opacity: 0.28, roughness: 0.06, metalness: 0, depthWrite: false }),
  clay: () => new THREE.MeshStandardMaterial({ color: "#8a8a90", roughness: 0.9, metalness: 0.05 }),
};

function rb(w, h, d, r = 0.06) {
  return new RoundedBoxGeometry(w, h, d, 3, Math.min(r, Math.min(w, h, d) / 2.5));
}

// A single LED bay: reflector cone + emitter disc + clear lens cap.
// Registers into group.userData.lenses for animation.
function ledBay(group, x, y, z, w, colorHex, moduleIndex, { lensH = 0.34, lensD = 1.1 } = {}) {
  const bay = new THREE.Group();
  bay.position.set(x, y, z);

  const refl = new THREE.Mesh(new THREE.ConeGeometry(w * 0.34, 0.22, 20, 1, true), MAT.reflector());
  refl.rotation.x = Math.PI;
  refl.position.y = -0.02;
  bay.add(refl);

  const emitterMat = new THREE.MeshStandardMaterial({ color: "#151518", emissive: colorHex, emissiveIntensity: 0.06, roughness: 0.3 });
  const emitter = new THREE.Mesh(new THREE.CylinderGeometry(w * 0.3, w * 0.3, 0.05, 20), emitterMat);
  emitter.position.y = 0.06;
  bay.add(emitter);

  const lens = new THREE.Mesh(rb(w * 0.92, lensH, lensD, 0.05), MAT.lens());
  lens.position.y = 0.16;
  bay.add(lens);

  const haloMat = new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.16, depthWrite: false });
  const halo = new THREE.Mesh(rb(w, lensH + 0.18, lensD + 0.12, 0.08), haloMat);
  halo.position.y = 0.18;
  halo.visible = false;
  bay.add(halo);

  const light = new THREE.PointLight(colorHex, 0, 8);
  light.position.y = 0.5;
  bay.add(light);

  group.add(bay);
  group.userData.lenses.push({ moduleIndex, colorHex, emitterMat, halo, light });
  return bay;
}

function baseGroup() {
  const g = new THREE.Group();
  g.userData.lenses = [];
  return g;
}

/** Set flash state: active = array of 0/1 per module index (or null = all off). clay = structure-only view. */
export function applyFlash(group, active, modules, clay = false) {
  group.userData.lenses.forEach((l) => {
    const on = !clay && !!active && (l.moduleIndex === -1 ? active.some(Boolean) : !!active[l.moduleIndex % active.length]);
    const hex = l.moduleIndex >= 0 && modules?.[l.moduleIndex % (modules.length || 1)] ? hexOf(modules[l.moduleIndex % (modules.length || 1)].color) : l.colorHex;
    l.emitterMat.emissive.set(hex);
    l.emitterMat.emissiveIntensity = on ? 3.4 : 0.06;
    l.light.color.set(hex);
    l.light.intensity = on ? 9 : 0;
    l.halo.material.color.set(hex);
    l.halo.visible = on;
  });
}

// ---------- FULL-SIZE LIGHTBAR (low-profile pursuit bar) ----------
export function buildFullBar(modules) {
  const g = baseGroup();
  const n = modules.length;
  const W = 8.2;

  const rail = new THREE.Mesh(rb(W, 0.42, 1.75, 0.12), MAT.housing());
  rail.position.y = 0.21;
  g.add(rail);
  const trim = new THREE.Mesh(rb(W * 0.99, 0.08, 1.8, 0.03), MAT.chrome());
  trim.position.y = 0.1;
  g.add(trim);
  [-W / 2 + 0.28, W / 2 - 0.28].forEach((x) => {
    const cap = new THREE.Mesh(rb(0.5, 0.5, 1.7, 0.16), MAT.housingMatte());
    cap.position.set(x, 0.34, 0);
    g.add(cap);
  });
  [-W / 2 + 0.9, W / 2 - 0.9].forEach((x) => {
    const foot = new THREE.Mesh(rb(0.55, 0.28, 2.1, 0.05), MAT.housingMatte());
    foot.position.set(x, -0.05, 0);
    g.add(foot);
  });

  const pad = 0.75;
  const inner = W - pad * 2;
  const bayW = Math.min(1.05, inner / n - 0.06);
  for (let i = 0; i < n; i++) {
    const x = n === 1 ? 0 : -inner / 2 + bayW / 2 + (i / (n - 1)) * (inner - bayW);
    const colorHex = hexOf(modules[i].color);
    ledBay(g, x, 0.44, 0, bayW, colorHex, i, { lensH: 0.32, lensD: 1.5 });
  }
  return g;
}

// ---------- MINI BAR (magnet-mount micro warning bar) ----------
export function buildMiniBar(modules) {
  const g = baseGroup();
  const n = modules.length;
  const W = 3.6;
  const rail = new THREE.Mesh(rb(W, 0.34, 1.35, 0.1), MAT.housing());
  rail.position.y = 0.17;
  g.add(rail);
  [-1.1, 1.1].forEach((x) => {
    const mag = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.24, 0.1, 16), MAT.housingMatte());
    mag.position.set(x, -0.05, 0);
    g.add(mag);
  });
  const bayW = Math.min(0.8, (W - 0.5) / n - 0.05);
  for (let i = 0; i < n; i++) {
    const x = n === 1 ? 0 : -W / 2 + 0.35 + bayW / 2 + (i / (n - 1)) * (W - 0.7 - bayW);
    ledBay(g, x, 0.36, 0, bayW, hexOf(modules[i].color), i, { lensH: 0.26, lensD: 1.1 });
  }
  return g;
}

// ---------- BEACON (classic pedestal dome) ----------
export function buildBeacon(modules) {
  const g = baseGroup();
  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.7, 0.22, 24), MAT.housing());
  base.position.y = 0.11;
  g.add(base);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.045, 10, 28), MAT.chrome());
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.24;
  g.add(ring);
  const colors = modules.length ? [hexOf(modules[0].color), hexOf(modules[Math.floor(modules.length / 2)]?.color || modules[0].color)] : ["#ff2d2d", "#2e6bff"];
  [-0.18, 0.18].forEach((x, i) => {
    const refl = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.3, 16, 1, true), MAT.reflector());
    refl.position.set(x, 0.55, 0);
    refl.rotation.z = i === 0 ? 0.5 : -0.5;
    g.add(refl);
    const em = new THREE.MeshStandardMaterial({ color: "#151518", emissive: colors[i], emissiveIntensity: 0.06 });
    const disc = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 10), em);
    disc.position.set(x, 0.5, 0);
    g.add(disc);
    const halo = new THREE.Mesh(new THREE.SphereGeometry(0.5, 14, 10), new THREE.MeshBasicMaterial({ color: colors[i], transparent: true, opacity: 0.14, depthWrite: false }));
    halo.position.set(x, 0.6, 0);
    halo.visible = false;
    g.add(halo);
    const light = new THREE.PointLight(colors[i], 0, 7);
    light.position.set(x, 0.8, 0);
    g.add(light);
    g.userData.lenses.push({ moduleIndex: i === 0 ? 0 : Math.floor(modules.length / 2), colorHex: colors[i], emitterMat: em, halo, light });
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.58, 28, 20, 0, Math.PI * 2, 0, Math.PI / 2), MAT.lens());
  dome.position.y = 0.28;
  g.add(dome);
  return g;
}

// ---------- DASH / DECK LIGHT (windshield unit) ----------
export function buildDashLight(modules) {
  const g = baseGroup();
  const W = 2.6;
  const body = new THREE.Mesh(rb(W, 0.5, 0.7, 0.1), MAT.housing());
  body.position.y = 0.25;
  g.add(body);
  const shroud = new THREE.Mesh(rb(W * 0.96, 0.34, 0.16, 0.05), MAT.housingMatte());
  shroud.position.set(0, 0.34, 0.3);
  g.add(shroud);
  [-0.8, 0.8].forEach((x) => {
    const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 0.06, 12), MAT.housingMatte());
    cup.position.set(x, 0.02, -0.28);
    g.add(cup);
  });
  const half = Math.ceil(modules.length / 2);
  modules.forEach((m, i) => {
    const side = i < half ? -1 : 1;
    const idx = i < half ? i : i - half;
    const cnt = i < half ? half : modules.length - half;
    const x = side * (0.45 + idx * (0.75 / Math.max(1, cnt)) + 0.35 / Math.max(1, cnt));
    ledBay(g, x, 0.5, 0.05, 0.5, hexOf(m.color), i, { lensH: 0.22, lensD: 0.5 });
  });
  return g;
}

// ---------- PERIMETER / GRILLE HEAD (surface-mount) ----------
export function buildPerimeterHead(modules) {
  const g = baseGroup();
  const flange = new THREE.Mesh(rb(1.0, 0.42, 0.16, 0.06), MAT.chrome());
  flange.position.y = 0.21;
  g.add(flange);
  const body = new THREE.Mesh(rb(0.86, 0.34, 0.3, 0.07), MAT.housing());
  body.position.set(0, 0.21, 0.08);
  g.add(body);
  const cols = modules.length >= 2 ? 2 : 1;
  for (let i = 0; i < cols; i++) {
    const x = cols === 1 ? 0 : i === 0 ? -0.2 : 0.2;
    ledBay(g, x, 0.24, 0.24, 0.34, hexOf(modules[i % modules.length]?.color || "red"), i % Math.max(1, modules.length), { lensH: 0.2, lensD: 0.18 });
  }
  return g;
}

// ---------- PUSH BAR (bumper guard, procedural steel) ----------
export function buildPushBar() {
  const g = baseGroup();
  const steel = MAT.housingMatte();
  const tube = (w, h, d, x, y, z, mat = steel) => {
    const m = new THREE.Mesh(rb(w, h, d, 0.04), mat);
    m.position.set(x, y, z);
    g.add(m);
    return m;
  };
  [-0.55, 0.55].forEach((x) => tube(0.14, 1.15, 0.14, x, 0.62, 0)); // uprights
  tube(1.45, 0.13, 0.13, 0, 1.1, 0); // top crossbar
  tube(1.45, 0.13, 0.13, 0, 0.55, 0); // mid crossbar
  [-0.55, 0.55].forEach((x) => tube(0.3, 0.1, 0.5, x, 0.12, -0.25)); // mounting plates
  [-0.85, 0.85].forEach((x) => { // wrap-around wings
    const wing = tube(0.13, 0.85, 0.13, x, 0.72, -0.12);
    wing.rotation.x = 0.35;
  });
  return g;
}

export const LIGHTBAR_MODELS = [
  { id: "fullbar", label: "Liberty Full Bar", desc: "Low-profile pursuit roof bar", build: buildFullBar, displaySize: 7 },
  { id: "minibar", label: "Micro Mini Bar", desc: "Magnet-mount warning bar", build: buildMiniBar, displaySize: 4.2 },
  { id: "beacon", label: "Rotator Beacon", desc: "Classic pedestal dome beacon", build: buildBeacon, displaySize: 2.1 },
  { id: "dash", label: "Dash / Deck Light", desc: "Windshield + rear deck unit", build: buildDashLight, displaySize: 3.4 },
  { id: "perimeter", label: "Perimeter Head", desc: "Surface-mount grille / fender head", build: buildPerimeterHead, displaySize: 1.7 },
];
